import { useMemo } from 'react';

import { Heading, NotificationBanner } from '@dbt-labs/sourdough';

import { ResourceCountCard } from '@/components/ResourceCountCard';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useEnvironmentSummary } from '@/hooks/discovery/environmentSummary';
import {
  NavigationResourceType,
  orderedNavigationResourceTypes,
} from '@/panel/ResourcesFilter/selectedResource';

type ResourceType = NavigationResourceType;

type ResourceCount = {
  resourceType: ResourceType;
  count: number;
};

export const allResourceTypes = orderedNavigationResourceTypes;

export const ProjectAssets = () => {
  const { accountId, environmentId } = useDbtCloudProject();

  const { data, fetching, error } = useEnvironmentSummary({
    accountId: accountId || 0,
    environmentId,
    pause: !accountId || !environmentId,
  });

  const resourceCounts: ResourceCount[] = useMemo(() => {
    const counts: Record<ResourceType, number> = {
      model: data?.model || 0,
      source: data?.source || 0,
      test: data?.test || 0,
      group: data?.groups || 0,
      exposure: data?.exposure || 0,
      metric: data?.metric || 0,
      semantic_model: data?.semantic_model || 0,
      seed: data?.seed || 0,
      macro: data?.macro || 0,
      snapshot: data?.snapshot || 0,
      saved_query: data?.saved_query || 0,
    };

    return Object.entries(counts).map(([resourceType, count]) => ({
      resourceType: resourceType as ResourceType,
      count,
    }));
  }, [data]);

  return (
    <div>
      <div className="overflow-hidden">
        <div className="mb-1">
          <Heading size="4">Project assets</Heading>
        </div>
        {error && (
          <NotificationBanner
            isGlobal={false}
            notification={{
              id: 'unableToFetchResourceCounts',
              type: 'error',
              header: 'Failed to retrieve resource counts',
              helperText: (
                <p>
                  An unexpected error occurred while retrieving resource counts. Please
                  refresh the page and try again.
                </p>
              ),
            }}
            className="mt-2"
          />
        )}
        <div className="xl:grid-cols-10 my-4 grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 ml:grid-cols-3 lg:grid-cols-5">
          {fetching || error
            ? allResourceTypes.map((resourceType) => (
                <div
                  key={`skeleton-${resourceType}`}
                  className="w-full"
                  data-testid={`resource-count-card-${resourceType}`}
                >
                  <ResourceCountCard
                    resourceType={resourceType}
                    skeleton={!error}
                    count={error ? 0 : undefined}
                  />
                </div>
              ))
            : resourceCounts.map((resourceCount: ResourceCount) => (
                <div
                  key={resourceCount.resourceType}
                  data-testid={`resource-count-card-${resourceCount.resourceType}`}
                  className="w-full"
                >
                  <ResourceCountCard
                    resourceType={resourceCount.resourceType}
                    count={resourceCount.count}
                    skeleton={false}
                  />
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};
